import CommonButton from 'components/Buttons/CommonButton';
import { DropDownOptions } from 'models/report';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAsync } from 'react-use';
import {
  ButtonsContainer,
  Right,
  RightTitle,
  RightWrapper,
  InputLabel,
  CheckboxLabel,
} from 'routes/Configurator/styles';
import SelectFilterPreference from 'routes/Report/components/SelectFilterPreference';
import SelectReport from 'routes/Report/components/SelectReport';
import services from 'services';
import actions from 'state/actions';
import selectors from 'state/selectors';
import styled from 'styled-components/macro';
import utils from 'utils';
import CronFrequencySelector from '../components/CronFrequencySelector';
import { FormValues } from './reportUtils';

export const ExtendedInputLabel = styled(InputLabel)`
  width: 200px;
  white-space: normal;
  overflow: hidden;
`;
export const ExtendedCheckboxLabel = styled(CheckboxLabel)`
  width: 200px;
  white-space: normal;
  overflow: hidden;
`;

interface IProps {
  values: FormValues;
  setFieldValue: Function;
  dirty: boolean;
  isValid: boolean;
  isValidating: boolean;
  submitForm: (e: any) => void;
}

type ReportOption = {
  idParent: number | string;
  label: string;
  value: string;
};

const FormReport = ({ values, setFieldValue, dirty, isValid, isValidating, submitForm }: IProps) => {
  const t = utils.intl.useTranslator();
  const dispatch = useDispatch();

  const formMode = useSelector(selectors.configurator.getFormModeReports);
  const optionsFilterPreference = useSelector(selectors.userReportSettings.getOptionsFilterPreference);
  const selectedFilterPreference = useSelector(selectors.userReportSettings.getSelectedFilterPreference);
  const idUser = useSelector(selectors.user.getIdUser);

  const [folderOptions, setFolderOptions] = useState<DropDownOptions[]>([]);
  const [reportOptions, setReportOptions] = useState<DropDownOptions[] | any[]>([]);
  const [allReportOptions, setAllReportOptions] = useState<any[]>([]);
  const selectedFolder = values['selectedFolder']?.value;

  useAsync(async () => {
    if (idUser) {
      try {
        const { data } = await services.getReports(idUser);
        let folderData = data.reports.map((folder) => ({
          value: folder.idReport.toString(),
          label: folder.folderName,
        }));
        folderData = folderData.filter((el) => el.value !== '0');
        folderData.push({ value: '0', label: 'No folder' });
        const fileData = data.reports.map(({ children }) =>
          children.map((child) => ({ idParent: child.idParent, value: child.idReport, label: child.reportName })),
        );
        setFolderOptions(folderData);
        setAllReportOptions(fileData);
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  useEffect(() => {
    const optionsBasedOnFolder = allReportOptions.filter(
      (el) => el.length && el[0].idParent.toString() === selectedFolder,
    );

    if (optionsBasedOnFolder.length > 0) {
      const reportList = optionsBasedOnFolder[0] as ReportOption[];
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const cleanedOptions = reportList.map(({ idParent, ...rest }) => rest);
      setReportOptions(cleanedOptions);
    } else {
      setReportOptions([]);
    }
  }, [selectedFolder, allReportOptions, setFieldValue]);

  const handleChangeFolder = (option: DropDownOptions) => {
    setFieldValue('selectedFolder', option);
    setFieldValue('selectedReport', { label: '', value: '' });
    dispatch(actions.userReportSettings.setOptionsFilterPreference([]));
  };

  const handleChangeReport = async (option: DropDownOptions) => {
    setFieldValue('selectedReport', option);

    const { data } = await services.loadFiltersPreference(
      parseInt(values['selectedFolder'].value),
      parseInt(option.value),
    );
    dispatch(actions.userReportSettings.setOptionsFilterPreference(data));
    dispatch(actions.userReportSettings.setSelectedFilterPreference(null));
  };

  const selectOptionFilterPreference = (option: { value: number; label: string } | null) => {
    if (!option) {
      dispatch(actions.userReportSettings.setSelectedFilterPreference(null));
      return;
    }

    const { value } = option;
    const { idUserReportSetting } = selectedFilterPreference ?? {};

    if (idUserReportSetting !== undefined && value === idUserReportSetting) return;

    const selectedOption = optionsFilterPreference.find((el) => el.idUserReportSetting === value);
    if (selectedOption) {
      dispatch(actions.userReportSettings.setSelectedFilterPreference(selectedOption));
    }
  };

  return (
    <RightWrapper>
      <RightTitle> {t(`${formMode}_setting`)}</RightTitle>
      <Right>
        <div style={{ margin: '10px 150px' }}>
          <SelectReport
            key={'select-report'}
            onChangeFolder={handleChangeFolder}
            onChangeReport={handleChangeReport}
            selectedFolder={values['selectedFolder']}
            selectedReport={values['selectedReport']}
            optionsFolder={folderOptions}
            optionsReport={reportOptions}
            disabled={formMode === 'edit'}
          />
          <div style={{ marginTop: '10px' }}>
            <>
              <SelectFilterPreference
                key={'select-filter-preference'}
                options={optionsFilterPreference}
                setOptions={(value: { value: number; label: string } | null) => {
                  selectOptionFilterPreference(value);
                  setFieldValue('selectedReportFilter', value);
                }}
                disabled={formMode === 'edit'}
              />
            </>
          </div>
        </div>
        <div style={{ margin: '10px 150px' }}>
          <CronFrequencySelector
            hasBox={true}
            enabled={values['enabled'] ? true : false}
            setEnabled={(value: boolean) => setFieldValue('enabled', value)}
            hour={values['hour']}
            setHour={(value: number) => setFieldValue('hour', value)}
            minute={values['minute']}
            setMinute={(value: number) => setFieldValue('minute', value)}
            allDays={values['allDays']}
            setAllDays={(value: boolean) => setFieldValue('allDays', value)}
            daysOfWeek={values['daysOfWeek']}
            setDaysOfWeek={(value: { label: string; value: number }[]) => setFieldValue('daysOfWeek', value)}
          />
        </div>
        <ButtonsContainer>
          <CommonButton
            id="saveButton"
            scope="primary"
            disabled={!dirty || !isValid || isValidating}
            type="submit"
            value={t(`${formMode}_settings`)}
            action={submitForm}
          />
          <CommonButton
            id="cancelButton"
            scope="secondary"
            type="button"
            value={t('cancel')}
            action={() => {
              dispatch(actions.configurator.setFormModeReport(null));
            }}
          />
        </ButtonsContainer>
      </Right>
    </RightWrapper>
  );
};
export default FormReport;
